import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { HelmetProvider } from "react-helmet-async";
import ScrollToTop from "./components/ScrollToTop";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { MarketingCopyProvider } from "./contexts/MarketingCopyContext";
import { AdminAuthProvider, useAdminAuth } from "./contexts/AdminAuthContext";
import { P150AuthProvider, useP150Auth } from "./contexts/P150AuthContext";
import { DWAuthProvider } from "./contexts/DWAuthContext";
import MirrorIndexGate from "./pages/mirror-index/MirrorIndexGate";
import { InkwayAuthProvider, useInkwayAuth } from "./contexts/InkwayAuthContext";
// Route factory is tiny; its heavy page components are lazy-split inside AdminRoutes.js
import { createProductAdminRoutes } from "./pages/admin/ProductAdminPortal";

import "@/App.css";
import { PageviewTracker } from "./lib/analytics";

// ---- Route-level pages are code-split (React.lazy). A first-time visitor to a
// public marketing page no longer downloads the admin/dashboard/partner apps. ----
const MyMirrorHome = lazy(() => import("./pages/MyMirrorHome"));
const HomeVariantA = lazy(() => import("./pages/sandbox/HomeVariantA"));
const HomeVariantAFull = lazy(() => import("./pages/sandbox/HomeVariantAFull"));
const HomeVariantB = lazy(() => import("./pages/sandbox/HomeVariantB"));
const HomeVariantC = lazy(() => import("./pages/sandbox/HomeVariantC"));
const AudioAbout = lazy(() => import("./pages/AudioAbout"));
const Landing = lazy(() => import("./pages/Landing"));
const Personality150Landing = lazy(() => import("./pages/Personality150Landing"));
const EIMirrorLanding = lazy(() => import("./pages/EIMirrorLanding"));
const P150Register = lazy(() => import("./pages/P150Register"));
const P150Login = lazy(() => import("./pages/P150Login"));
const P150Dashboard = lazy(() => import("./pages/P150Dashboard"));
const P150Assessment = lazy(() => import("./pages/P150Assessment"));
const P150Report = lazy(() => import("./pages/P150Report"));
const Login = lazy(() => import("./pages/Login"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const UnifiedLogin = lazy(() => import("./pages/UnifiedLogin"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const Register = lazy(() => import("./pages/Register"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Products = lazy(() => import("./pages/Products"));
const LeadershipSuite = lazy(() => import("./pages/LeadershipSuite"));
const Methodology = lazy(() => import("./pages/Methodology"));
const Validity = lazy(() => import("./pages/Validity"));
const OurVoice = lazy(() => import("./pages/OurVoice"));
const StartAssessment = lazy(() => import("./pages/StartAssessment"));
const Assessment = lazy(() => import("./pages/Assessment"));
const SPIAssessment = lazy(() => import("./pages/SPIAssessment"));
const SJTAssessment = lazy(() => import("./pages/SJTAssessment"));
const EQLeadAssessment = lazy(() => import("./pages/EQLeadAssessment"));
const EQLeadBrochure = lazy(() => import("./pages/EQLeadBrochure"));
const EQLeadIntro = lazy(() => import("./pages/EQLeadIntro"));
const GapAnalysis = lazy(() => import("./pages/GapAnalysis"));
const FinalReport = lazy(() => import("./pages/FinalReport"));
const About = lazy(() => import("./pages/About"));
const Payment = lazy(() => import("./pages/Payment"));
const Report = lazy(() => import("./pages/Report"));
const InviteRaters = lazy(() => import("./pages/InviteRaters"));
const Invitations = lazy(() => import("./pages/Invitations"));
const AdminDashboardOld = lazy(() => import("./pages/AdminDashboard"));
const CohortAnalytics = lazy(() => import("./pages/CohortAnalytics"));

// Admin Portal Pages
const AdminLogin = lazy(() => import("./pages/admin/AdminLogin"));
const AdminResetPassword = lazy(() => import("./pages/admin/AdminResetPassword"));
const AdminLayout = lazy(() => import("./pages/admin/AdminLayout"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminUsers = lazy(() => import("./pages/admin/AdminUsers"));
const AdminUserDetail = lazy(() => import("./pages/admin/AdminUserDetail"));
const AdminAssessments = lazy(() => import("./pages/admin/AdminAssessments"));
const AdminManagement = lazy(() => import("./pages/admin/AdminManagement"));
const AdminOrganisations = lazy(() => import("./pages/admin/AdminOrganisations"));
const AdminAuditLogs = lazy(() => import("./pages/admin/AdminAuditLogs"));
const AdminSettings = lazy(() => import("./pages/admin/AdminSettings"));

// EI Mirror
const EIMirrorAssessment = lazy(() => import("./pages/EIMirrorAssessment"));
const EIMirrorReport = lazy(() => import("./pages/EIMirrorReport"));

// Voice Mirror v1.0
const VoiceMirrorLanding = lazy(() => import("./pages/VoiceMirrorLanding"));
const VoiceMirrorAssessment = lazy(() => import("./pages/VoiceMirrorAssessment"));
const VoiceMirrorReport = lazy(() => import("./pages/VoiceMirrorReport"));

// Information Paper (shareable positioning doc)
const InformationPaper = lazy(() => import("./pages/InformationPaper"));

// The Leadership Delta — presentation viewer + explainer video
const LeadershipDeckPage = lazy(() => import("./pages/LeadershipDeckPage"));
const PdfViewerPage = lazy(() => import("./pages/PdfViewerPage"));
const MarketPotentialReport = lazy(() => import("./pages/MarketPotentialReport"));

// White-label DEMO — The Test Company (fully mocked sales asset)
const TheTestCompany = lazy(() => import("./pages/demo/theTestCompany/TheTestCompany"));
const TCInnerPage = lazy(() => import("./pages/demo/theTestCompany/TCInnerPage"));
const TCEmailInviteMockup = lazy(() => import("./pages/demo/theTestCompany/EmailInviteMockup"));
const TCCandidateLogin = lazy(() => import("./pages/demo/theTestCompany/CandidateLogin"));
const TCQuestionnaire = lazy(() => import("./pages/demo/theTestCompany/Questionnaire"));
const TCThankYou = lazy(() => import("./pages/demo/theTestCompany/ThankYou"));
const TCSampleReport = lazy(() => import("./pages/demo/theTestCompany/SampleReport"));
const UpAGear = lazy(() => import("./pages/demo/upagear/UpAGear"));
const UAGInnerPage = lazy(() => import("./pages/demo/upagear/UAGInnerPage"));
const UAGEmailInviteMockup = lazy(() => import("./pages/demo/upagear/UAGEmailInviteMockup"));
const UAGCandidateLogin = lazy(() => import("./pages/demo/upagear/UAGCandidateLogin"));
const UAGQuestionnaire = lazy(() => import("./pages/demo/upagear/UAGQuestionnaire"));
const UAGThankYou = lazy(() => import("./pages/demo/upagear/UAGThankYou"));
const UAGSampleReport = lazy(() => import("./pages/demo/upagear/UAGSampleReport"));
const UAGMakeThisYours = lazy(() => import("./pages/demo/upagear/UAGMakeThisYours"));

// Unified Dashboard
const UnifiedDashboard = lazy(() => import("./pages/UnifiedDashboard"));

// Org Leader Portal
const OrgLeaderPortal = lazy(() => import("./pages/OrgLeaderPortal"));
const UnifiedOrgLeaderPortal = lazy(() => import("./pages/UnifiedOrgLeaderPortal"));
const OrgLeaderRegister = lazy(() => import("./pages/OrgLeaderRegister"));
const SJTProductDocument = lazy(() => import("./pages/SJTProductDocument"));

// Sample Report Page
const SampleReportPage = lazy(() => import("./pages/SampleReportPage"));
const TeamMirrorLanding = lazy(() => import("./pages/TeamMirrorLanding"));
const SampleTeamReport = lazy(() => import("./pages/SampleTeamReport"));
const TeamMirrorJoin = lazy(() => import("./pages/TeamMirrorJoin"));
const OrgLeaderTeamReport = lazy(() => import("./pages/OrgLeaderTeamReport"));
const OrgLeaderReadinessReport = lazy(() => import("./pages/OrgLeaderReadinessReport"));
const OrgLeaderPathway = lazy(() => import("./pages/OrgLeaderPathway"));
const OrgLeaderPathwayReport = lazy(() => import("./pages/OrgLeaderPathwayReport"));
const ReadinessRate = lazy(() => import("./pages/readiness/ReadinessRate"));
const P150SwitchModule = lazy(() => import("./pages/p150switch/P150SwitchModule"));
const OrgSurveyRespond = lazy(() => import("./pages/orgsurvey/OrgSurveyRespond"));
const OrgSurveyReport = lazy(() => import("./pages/OrgSurveyReport"));
const Resources = lazy(() => import("./pages/Resources"));
const TheWrongDriver = lazy(() => import("./pages/TheWrongDriver"));
const TheWrongDriverPredictions = lazy(() => import("./pages/TheWrongDriverPredictions"));
const ContactUs = lazy(() => import("./pages/ContactUs"));
const ForCoaches = lazy(() => import("./pages/ForCoaches"));
const ForOrganisations = lazy(() => import("./pages/ForOrganisations"));
const RecruitersLanding = lazy(() => import("./pages/RecruitersLanding"));
const RecruitersSuccess = lazy(() => import("./pages/RecruitersSuccess"));
const RecruitersPortal = lazy(() => import("./pages/RecruitersPortal"));
const SJTExplainer = lazy(() => import("./pages/SJTExplainer"));
const SJTTaster = lazy(() => import("./pages/SJTTaster"));
const SJTRedeem = lazy(() => import("./pages/SJTRedeem"));
const WhitelabelSamplePortal = lazy(() => import("./pages/WhitelabelSamplePortal"));
const BetaInvite = lazy(() => import("./pages/BetaInvite"));
const EQLeadIntegratedSample = lazy(() => import("./pages/EQLeadIntegratedSample"));

// Super Admin Portal
const SuperAdminP150Report = lazy(() => import("./pages/SuperAdminP150Report"));
const SuperAdminPortal = lazy(() => import("./pages/SuperAdminPortal"));

// Public partner-facing integration page
const PartnerIntegrate = lazy(() => import("./pages/PartnerIntegrate"));

// EQ-Lead 120 Integrated Report
const EQLeadIntegratedReport = lazy(() => import("./pages/admin/EQLeadIntegratedReport"));

// Payment Pages (named exports)
const PaymentSuccess = lazy(() => import("./pages/PaymentPages").then(m => ({ default: m.PaymentSuccess })));
const PaymentCancel = lazy(() => import("./pages/PaymentPages").then(m => ({ default: m.PaymentCancel })));

// Dating Wellness Product
const DWLoginPage = lazy(() => import("./pages/dating/LoginPage"));
const DWRegisterPage = lazy(() => import("./pages/dating/RegisterPage"));
const DWQuizPage = lazy(() => import("./pages/dating/QuizPage"));
const DWResultsPage = lazy(() => import("./pages/dating/ResultsPage"));
const DWDashboardPage = lazy(() => import("./pages/dating/DashboardPage"));
const DWUpgradePage = lazy(() => import("./pages/dating/UpgradePage"));
// (unlinked Jul 2026) DWHowItWorksPage kept in code; route redirects to /mirror-index
const DWSampleReportsPage = lazy(() => import("./pages/dating/DWSampleReportsPage"));
const DWJournalPage = lazy(() => import("./pages/dating/JournalPage"));
const DWSharedResultPage = lazy(() => import("./pages/dating/SharedResultPage"));
const PersonalityMirrorQuiz = lazy(() => import("./pages/dating/PersonalityMirrorQuiz"));
const PersonalityMirrorResults = lazy(() => import("./pages/dating/PersonalityMirrorResults"));
const EmotionalMirrorQuiz = lazy(() => import("./pages/dating/EmotionalMirrorQuiz"));
const EmotionalMirrorResults = lazy(() => import("./pages/dating/EmotionalMirrorResults"));

// Mirror Index — new canonical pages (Feb 2026 rebrand)
const MirrorIndexLanding = lazy(() => import("./pages/mirror-index/MirrorIndexLanding"));
const MirrorIndexArchetypes = lazy(() => import("./pages/mirror-index/MirrorIndexArchetypes"));
const MirrorIndexQuizSelect = lazy(() => import("./pages/mirror-index/MirrorIndexQuizSelect"));
const MirrorIndexForBusiness = lazy(() => import("./pages/mirror-index/MirrorIndexForBusiness"));
// (unlinked Jul 2026) MirrorIndexMethodology kept in code; route redirects to /mirror-index
const MirrorIndexAbout = lazy(() => import("./pages/mirror-index/MirrorIndexAbout"));
const MirrorIndexOnboarding = lazy(() => import("./pages/mirror-index/MirrorIndexOnboarding"));
const MirrorIndexPhilosophy = lazy(() => import("./pages/mirror-index/MirrorIndexPhilosophy"));
const MirrorIndexHowItWorks = lazy(() => import("./pages/mirror-index/MirrorIndexHowItWorks"));
const MirrorIndexWhy = lazy(() => import("./pages/mirror-index/MirrorIndexWhy"));
const MirrorIndexScience = lazy(() => import("./pages/mirror-index/MirrorIndexScience"));
const MirrorIndexFAQ = lazy(() => import("./pages/mirror-index/MirrorIndexFAQ"));
const MirrorIndexArchetypeDetail = lazy(() => import("./pages/mirror-index/MirrorIndexArchetypeDetail"));
const MirrorIndexLearn = lazy(() => import("./pages/mirror-index/MirrorIndexLearn"));
const MirrorIndexLearnArticle = lazy(() => import("./pages/mirror-index/MirrorIndexLearnArticle"));
const MirrorIndexPostBreakup = lazy(() => import("./pages/mirror-index/MirrorIndexPostBreakup"));
// (unlinked Jul 2026) MirrorIndexReliability kept in code; route redirects to /mirror-index
const MyMirrorPositioning = lazy(() => import("./pages/mirror-index/MyMirrorPositioning"));
const DatingWellnessLegacyRedirect = lazy(() => import("./pages/mirror-index/LegacyRedirect"));

// The Mirror — remodel (independent namespace, Aug 2026)
const MirrorLanding = lazy(() => import("./pages/mirror/MirrorLanding"));
const MirrorRunner = lazy(() => import("./pages/mirror/MirrorRunner"));
const MirrorResults = lazy(() => import("./pages/mirror/MirrorResults"));
const MirrorMirrors = lazy(() => import("./pages/mirror/MirrorMirrors"));
const MirrorLearnHub = lazy(() => import("./pages/mirror/MirrorLearn"));
const MirrorLearnEssay = lazy(() => import("./pages/mirror/MirrorLearnArticle"));
const MirrorMethodologyPage = lazy(() => import("./pages/mirror/MirrorMethodology"));
const MirrorSafetyPage = lazy(() => import("./pages/mirror/MirrorSafety"));
const MirrorFlagCheck = lazy(() => import("./pages/mirror/MirrorFlagCheck"));
const MirrorPromisePage = lazy(() => import("./pages/mirror/MirrorPromise"));
const MiraLunaLanding = lazy(() => import("./pages/mira-luna/Landing"));
const MiraLunaQuiz = lazy(() => import("./pages/mira-luna/Quiz"));
const MiraLunaResults = lazy(() => import("./pages/mira-luna/Results"));
const MiraLunaAbout = lazy(() => import("./pages/mira-luna/About"));
const MiraLunaArchetypes = lazy(() => import("./pages/mira-luna/Archetypes"));
const AdminSSOLanding = lazy(() => import("./pages/AdminSSOLanding"));
const DWTermsPage = lazy(() => import("./pages/dating/TermsPage"));
const DWPrivacyPage = lazy(() => import("./pages/dating/PrivacyPage"));
const DWAdminLoginPage = lazy(() => import("./pages/dating/AdminLoginPage"));
const DWAdminDashboardPage = lazy(() => import("./pages/dating/AdminDashboardPage"));

// Inkway / Quilliam Writing Platform
const InkwayLanding = lazy(() => import("./pages/inkway/Landing"));
const InkwayLogin = lazy(() => import("./pages/inkway/Login"));
const InkwayRegister = lazy(() => import("./pages/inkway/Register"));
const InkwayDashboard = lazy(() => import("./pages/inkway/Dashboard"));
const InkwayOnboarding = lazy(() => import("./pages/inkway/Onboarding"));
const InkwayPricing = lazy(() => import("./pages/inkway/Pricing"));
const InkwayCreditHistory = lazy(() => import("./pages/inkway/CreditHistory"));
const InkwayCourses = lazy(() => import("./pages/inkway/Courses"));
const InkwayCourseDetail = lazy(() => import("./pages/inkway/CourseDetail"));
const InkwayConciergeLanding = lazy(() => import("./pages/inkway/ConciergeLanding"));
const InkwayConciergeIntake = lazy(() => import("./pages/inkway/ConciergeIntake"));
const InkwayConciergeSuccess = lazy(() => import("./pages/inkway/ConciergeSuccess"));
const InkwayConciergeSLA = lazy(() => import("./pages/inkway/ConciergeSLA"));
const InkwayClientPortal = lazy(() => import("./pages/inkway/ClientPortal"));
const InkwayForgotPassword = lazy(() => import("./pages/inkway/ForgotPassword"));
const InkwayResetPassword = lazy(() => import("./pages/inkway/ResetPassword"));
const InkwayTerms = lazy(() => import("./pages/inkway/TermsConditions"));
const InkwayPrivacy = lazy(() => import("./pages/inkway/PrivacyPolicy"));
const InkwayAdminLayout = lazy(() => import("./pages/inkway/admin/AdminLayout"));
const InkwayAdminDashboard = lazy(() => import("./pages/inkway/admin/AdminDashboard"));
const InkwayAdminPricing = lazy(() => import("./pages/inkway/admin/AdminPricing"));
const InkwayAdminProjects = lazy(() => import("./pages/inkway/admin/AdminProjects"));
const InkwayAdminSettings = lazy(() => import("./pages/inkway/admin/AdminSettings"));

// Suspense fallback shown while a route chunk downloads.
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-slate-50">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2563EB]"></div>
  </div>
);

// Protected Route wrapper
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2563EB]"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

// Admin Protected Route
const AdminProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAdminAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/admin/login" replace />;
  }
  
  return children;
};

// P150 Protected Route
const P150ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useP150Auth();
  if (loading) return <div className="min-h-screen flex items-center justify-center bg-sand"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#D8A47F]"></div></div>;
  if (!isAuthenticated) return <Navigate to="/personality-150/login" replace />;
  return children;
};

// Inkway Protected Route
const InkwayProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useInkwayAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center bg-warm-white"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-oak"></div></div>;
  if (!isAuthenticated) return <Navigate to="/inkway/login" replace />;
  return children;
};

// Inkway Admin Protected Route
const InkwayAdminProtectedRoute = ({ children }) => {
  const { user, isAuthenticated, loading } = useInkwayAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center bg-warm-white"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-oak"></div></div>;
  if (!isAuthenticated || user?.role !== 'admin') return <Navigate to="/inkway/login" replace />;
  return children;
};

function App() {
  return (
    <HelmetProvider>
    <MarketingCopyProvider>
    <AuthProvider>
    <DWAuthProvider>
      <AdminAuthProvider>
        <P150AuthProvider>
        <InkwayAuthProvider>
        <BrowserRouter>
          <ScrollToTop />
          <PageviewTracker />
          <Toaster 
            position="top-right" 
            richColors 
            closeButton
            toastOptions={{
              style: {
                fontFamily: 'Inter, sans-serif',
              },
            }}
          />
          <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/sandbox/home-a" element={<HomeVariantA />} />
            <Route path="/sandbox/home-a-full" element={<HomeVariantAFull sandbox />} />
            <Route path="/sandbox/home-b" element={<HomeVariantB />} />
            <Route path="/sandbox/home-c" element={<HomeVariantC />} />
            {/* Parent Brand */}
            <Route path="/" element={<HomeVariantAFull />} />
            <Route path="/home-classic" element={<MyMirrorHome />} />
            
            {/* Product Landing Pages */}
            <Route path="/eqlead" element={<Landing />} />
            <Route path="/resources" element={<Resources />} />
            <Route path="/the-wrong-driver" element={<TheWrongDriver />} />
            <Route path="/the-wrong-driver/predictions" element={<TheWrongDriverPredictions />} />
            <Route path="/contact" element={<ContactUs />} />
            <Route path="/for-coaches" element={<ForCoaches />} />
            <Route path="/for-organisations" element={<ForOrganisations />} />
            <Route path="/recruiters" element={<RecruitersLanding />} />
            <Route path="/for-recruiters" element={<Navigate to="/recruiters" replace />} />
            <Route path="/recruiters/success" element={<RecruitersSuccess />} />
            <Route path="/recruiters/portal/:token" element={<RecruitersPortal />} />
            <Route path="/sjt" element={<SJTExplainer />} />
            <Route path="/sjt/try" element={<SJTTaster />} />
            <Route path="/sjt/redeem" element={<SJTRedeem />} />
            <Route path="/sjt-assessment/redeem" element={<SJTRedeem />} />
            <Route path="/sjt-assessment" element={<SJTAssessment />} />
            <Route path="/sample/whitelabel" element={<WhitelabelSamplePortal />} />
            <Route path="/beta" element={<BetaInvite />} />
            <Route path="/p150-switch/:token" element={<P150SwitchModule />} />
            <Route path="/personality-150" element={<Personality150Landing />} />
            <Route path="/ei-mirror" element={<EIMirrorLanding />} />

            {/* Voice Mirror v1.0 — email-gated free pilot */}
            <Route path="/voice-mirror" element={<VoiceMirrorLanding />} />
            <Route path="/voice-mirror/assessment" element={<VoiceMirrorAssessment />} />
            <Route path="/voice-mirror/report/:sessionId" element={<VoiceMirrorReport />} />

            {/* Shareable positioning paper */}
            <Route path="/information-paper" element={<InformationPaper />} />

            {/* The Leadership Delta — in-browser presentation */}
            <Route path="/resources/leadership-delta" element={<LeadershipDeckPage />} />
            <Route path="/resources/view/:key" element={<PdfViewerPage />} />
            <Route path="/market-potential" element={<MarketPotentialReport />} />

            {/* White-label DEMO — The Test Company (mocked) */}
            <Route path="/demo/the-test-company" element={<TheTestCompany />} />
            <Route path="/demo/the-test-company/about" element={<TCInnerPage slug="about" />} />
            <Route path="/demo/the-test-company/executive-coaching" element={<TCInnerPage slug="executive-coaching" />} />
            <Route path="/demo/the-test-company/recruitment" element={<TCInnerPage slug="recruitment" />} />
            <Route path="/demo/the-test-company/growth" element={<TCInnerPage slug="growth" />} />
            <Route path="/demo/the-test-company/contact" element={<TCInnerPage slug="contact" />} />
            <Route path="/demo/the-test-company/invite" element={<TCEmailInviteMockup />} />
            <Route path="/demo/the-test-company/start" element={<TCCandidateLogin />} />
            <Route path="/demo/the-test-company/assessment" element={<TCQuestionnaire />} />
            <Route path="/demo/the-test-company/thank-you" element={<TCThankYou />} />
            <Route path="/demo/the-test-company/report" element={<TCSampleReport />} />
            <Route path="/demo/upagear" element={<UpAGear />} />
            <Route path="/demo/upagear/coaching-solutions" element={<UAGInnerPage slug="coaching-solutions" />} />
            <Route path="/demo/upagear/the-app" element={<UAGInnerPage slug="the-app" />} />
            <Route path="/demo/upagear/our-coaches" element={<UAGInnerPage slug="our-coaches" />} />
            <Route path="/demo/upagear/roi" element={<UAGInnerPage slug="roi" />} />
            <Route path="/demo/upagear/about" element={<UAGInnerPage slug="about" />} />
            <Route path="/demo/upagear/contact" element={<UAGInnerPage slug="contact" />} />
            <Route path="/demo/upagear/invite" element={<UAGEmailInviteMockup />} />
            <Route path="/demo/upagear/start" element={<UAGCandidateLogin />} />
            <Route path="/demo/upagear/assessment" element={<UAGQuestionnaire />} />
            <Route path="/demo/upagear/thank-you" element={<UAGThankYou />} />
            <Route path="/demo/upagear/report" element={<UAGSampleReport />} />
            <Route path="/demo/upagear/make-this-yours" element={<UAGMakeThisYours />} />


            {/* Sample Report Pages */}
            <Route path="/team-mirror" element={<TeamMirrorLanding />} />
            <Route path="/team-mirror/join/:memberToken" element={<TeamMirrorJoin />} />
            <Route path="/sample/team" element={<SampleTeamReport />} />
            <Route path="/sample/eqlead" element={<EQLeadIntegratedSample />} />
            <Route path="/sample/p150" element={<SampleReportPage product="p150" />} />
            <Route path="/sample/eimirror" element={<SampleReportPage product="eimirror" />} />
            <Route path="/sample/dating" element={<SampleReportPage product="dating" />} />
            
            <Route path="/login" element={<UnifiedLogin />} />
            <Route path="/eqlead-login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/terms-of-service" element={<TermsOfService />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/register" element={<Register />} />
            <Route path="/methodology" element={<Methodology />} />
            <Route path="/validity" element={<Validity />} />
            <Route path="/our-voice" element={<OurVoice />} />
            <Route path="/pricing" element={<Navigate to="/" replace />} />
            <Route path="/about" element={<About />} />
            <Route path="/payment-success" element={<PaymentSuccess />} />
            <Route path="/payment" element={
              <ProtectedRoute>
                <Payment />
              </ProtectedRoute>
            } />
            
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            } />
            
            <Route path="/products" element={
              <ProtectedRoute>
                <Products />
              </ProtectedRoute>
            } />
            
            <Route path="/leadership-suite" element={
              <ProtectedRoute>
                <LeadershipSuite />
              </ProtectedRoute>
            } />
            
            <Route path="/assessment/new/:type" element={
              <ProtectedRoute>
                <StartAssessment />
              </ProtectedRoute>
            } />
            
            <Route path="/assessment/:assessmentId" element={
              <ProtectedRoute>
                <Assessment />
              </ProtectedRoute>
            } />
            
            <Route path="/assessment-sjt/:assessmentId" element={
              <ProtectedRoute>
                <SJTAssessment />
              </ProtectedRoute>
            } />
            
            <Route path="/assessment/new/sjt" element={
              <ProtectedRoute>
                <SJTAssessment />
              </ProtectedRoute>
            } />
            
            <Route path="/assessment-spi/:assessmentId" element={
              <ProtectedRoute>
                <SPIAssessment />
              </ProtectedRoute>
            } />
            
            <Route path="/spi" element={
              <ProtectedRoute>
                <SPIAssessment />
              </ProtectedRoute>
            } />
            
            <Route path="/eqlead-brochure" element={<EQLeadBrochure />} />
            
            <Route path="/assessment/new/eqlead" element={
              <ProtectedRoute>
                <EQLeadIntro />
              </ProtectedRoute>
            } />
            
            <Route path="/assessment-eqlead/:assessmentId" element={
              <ProtectedRoute>
                <EQLeadAssessment />
              </ProtectedRoute>
            } />
            
            <Route path="/report/:reportId" element={
              <ProtectedRoute>
                <Report />
              </ProtectedRoute>
            } />
            
            <Route path="/gap-analysis" element={
              <ProtectedRoute>
                <GapAnalysis />
              </ProtectedRoute>
            } />
            
            <Route path="/final-report" element={
              <ProtectedRoute>
                <FinalReport />
              </ProtectedRoute>
            } />
            
            <Route path="/invite-raters/:assessmentId" element={
              <ProtectedRoute>
                <InviteRaters />
              </ProtectedRoute>
            } />
            
            <Route path="/invitations" element={
              <ProtectedRoute>
                <Invitations />
              </ProtectedRoute>
            } />
            
            {/* Legacy Admin Route */}
            <Route path="/admin-old" element={
              <ProtectedRoute allowedRoles={["admin", "hr"]}>
                <AdminDashboardOld />
              </ProtectedRoute>
            } />
            
            <Route path="/cohort/:cohortId" element={
              <ProtectedRoute allowedRoles={["admin", "hr", "coach"]}>
                <CohortAnalytics />
              </ProtectedRoute>
            } />
            
            {/* Admin Portal Routes - Separate Auth System */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin/reset-password" element={<AdminResetPassword />} />
            <Route path="/admin" element={
              <AdminProtectedRoute>
                <AdminLayout />
              </AdminProtectedRoute>
            }>
              <Route index element={<Navigate to="/admin/dashboard" replace />} />
              <Route path="dashboard" element={<AdminDashboard />} />
              <Route path="users" element={<AdminUsers />} />
              <Route path="users/:userId" element={<AdminUserDetail />} />
              <Route path="assessments" element={<AdminAssessments />} />
              <Route path="admins" element={<AdminManagement />} />
              <Route path="organisations" element={<AdminOrganisations />} />
              <Route path="audit-logs" element={<AdminAuditLogs />} />
              <Route path="settings" element={<AdminSettings />} />
            </Route>
            
            {/* Personality 150 User Routes */}
            <Route path="/personality-150/register" element={<P150Register />} />
            <Route path="/personality-150/login" element={<P150Login />} />
            <Route path="/personality-150/dashboard" element={<P150ProtectedRoute><P150Dashboard /></P150ProtectedRoute>} />
            <Route path="/personality-150/assessment/:assessmentId" element={<P150ProtectedRoute><P150Assessment /></P150ProtectedRoute>} />
            <Route path="/personality-150/report/:reportId" element={<P150ProtectedRoute><P150Report /></P150ProtectedRoute>} />
            
            {/* EI Mirror Routes */}
            <Route path="/eimirror" element={<EIMirrorAssessment />} />
            <Route path="/eimirror/report" element={<EIMirrorReport />} />

            {/* Unified Admin Dashboard */}
            <Route path="/unified-dashboard" element={<UnifiedDashboard />} />

            {/* Org Leader Portal — Option B Universal (card-grid landing) */}
            <Route path="/org-leader" element={<UnifiedOrgLeaderPortal />} />
            <Route path="/org-leader/team-mirror/:teamId/report" element={<OrgLeaderTeamReport />} />
            <Route path="/org-leader/readiness/:campaignId/report" element={<OrgLeaderReadinessReport />} />
            <Route path="/org-leader/org-survey/:surveyId/report" element={<OrgSurveyReport />} />
            <Route path="/org-leader/pathways/:id" element={<OrgLeaderPathway />} />
            <Route path="/org-leader/pathways/:id/report" element={<OrgLeaderPathwayReport />} />
            <Route path="/readiness/rate/:token" element={<ReadinessRate />} />
            <Route path="/org-survey/respond/:token" element={<OrgSurveyRespond />} />
            <Route path="/org-leader/register" element={<OrgLeaderRegister />} />
            <Route path="/org-leader/product/:productKey" element={<UnifiedOrgLeaderPortal />} />
            <Route path="/org-leader/legacy" element={<OrgLeaderPortal />} />

            {/* Stand-alone, unlinked SJT product document (direct URL only) */}
            <Route path="/sjt-product-document" element={<SJTProductDocument />} />

            <Route path="/super-admin" element={<SuperAdminPortal />} />
            <Route path="/super-admin/p150-report/:reportId" element={<SuperAdminP150Report />} />
            <Route path="/eqlead/admin/candidate/:candidateId/integrated" element={<EQLeadIntegratedReport />} />
            <Route path="/partners/integrate" element={<PartnerIntegrate />} />
            <Route path="/payment/success" element={<PaymentSuccess />} />
            <Route path="/payment/cancel" element={<PaymentCancel />} />

            {/* Product Admin Portals */}
            {createProductAdminRoutes('p150', 'Personality 150', '#D8A47F')}
            {createProductAdminRoutes('eim', 'My EI Mirror', '#4A90D9')}

            {/* Mirror Index Product Routes (Feb 2026 rebrand — canonical paths) */}
            {/* Public marketing pages — gated behind a shared beta-access code (Jul 2026, temporary) */}
            <Route element={<MirrorIndexGate />}>
              <Route path="/mirror-index" element={<MirrorIndexLanding />} />
              <Route path="/mirror-index/archetypes" element={<MirrorIndexArchetypes />} />
              <Route path="/mirror-index/archetypes/:slug" element={<MirrorIndexArchetypeDetail />} />
              <Route path="/mirror-index/quiz-select" element={<MirrorIndexQuizSelect />} />
              <Route path="/mirror-index/pricing" element={<MirrorIndexQuizSelect />} />
              <Route path="/mirror-index/for-business" element={<MirrorIndexForBusiness />} />
              <Route path="/mirror-index/how-it-works" element={<MirrorIndexHowItWorks />} />
              <Route path="/mirror-index/why" element={<MirrorIndexWhy />} />
              <Route path="/mirror-index/philosophy" element={<MirrorIndexPhilosophy />} />
              <Route path="/mirror-index/science" element={<MirrorIndexScience />} />
              <Route path="/mirror-index/faq" element={<MirrorIndexFAQ />} />
              <Route path="/mirror-index/learn" element={<MirrorIndexLearn />} />
              <Route path="/mirror-index/learn/:slug" element={<MirrorIndexLearnArticle />} />
              <Route path="/mirror-index/post-breakup" element={<MirrorIndexPostBreakup />} />
              <Route path="/mirror-index/about" element={<MirrorIndexAbout />} />
              <Route path="/mirror-index/samples" element={<DWSampleReportsPage />} />
              <Route path="/mirror-index/reports" element={<DWSampleReportsPage />} />
            </Route>
            <Route path="/mirror-index/methodology" element={<Navigate to="/mirror-index/science" replace />} />
            <Route path="/mirror-index/reliability" element={<Navigate to="/mirror-index/science" replace />} />

            {/* The Mirror — remodel (independent site namespace; merged/commercialised later) */}
            <Route path="/mirror" element={<MirrorLanding />} />
            <Route path="/mirror/take/:instrument" element={<MirrorRunner />} />
            <Route path="/mirror/results/:sessionId" element={<MirrorResults />} />
            <Route path="/mirror/mirrors" element={<MirrorMirrors />} />
            <Route path="/mirror/learn" element={<MirrorLearnHub />} />
            <Route path="/mirror/learn/:slug" element={<MirrorLearnEssay />} />
            <Route path="/mirror/methodology" element={<MirrorMethodologyPage />} />
            <Route path="/mirror/safety" element={<MirrorSafetyPage />} />
            <Route path="/mirror/flag-check" element={<MirrorFlagCheck />} />
            <Route path="/mirror/promise" element={<MirrorPromisePage />} />
            <Route path="/mirror-index/onboarding" element={<MirrorIndexOnboarding />} />
            <Route path="/positioning" element={<MyMirrorPositioning />} />
            <Route path="/audio-about" element={<AudioAbout />} />
            {/* Reuse existing DW components for app screens; rebrand happens in the components themselves */}
            <Route path="/mirror-index/login" element={<DWLoginPage />} />
            <Route path="/mirror-index/register" element={<DWRegisterPage />} />
            <Route path="/mirror-index/quiz/:quizType" element={<DWQuizPage />} />
            <Route path="/mirror-index/results/:resultId" element={<DWResultsPage />} />
            <Route path="/mirror-index/dashboard" element={<DWDashboardPage />} />
            <Route path="/mirror-index/upgrade/:resultId?" element={<DWUpgradePage />} />
            <Route path="/mirror-index/journal" element={<DWJournalPage />} />
            <Route path="/mirror-index/share/:shareId" element={<DWSharedResultPage />} />
            <Route path="/mirror-index/personality-quiz" element={<PersonalityMirrorQuiz />} />
            <Route path="/mirror-index/personality-results/:resultId" element={<PersonalityMirrorResults />} />
            <Route path="/mirror-index/emotional-quiz" element={<EmotionalMirrorQuiz />} />
            <Route path="/mirror-index/emotional-results/:resultId" element={<EmotionalMirrorResults />} />
            <Route path="/mirror-index/terms" element={<DWTermsPage />} />
            <Route path="/mirror-index/privacy" element={<DWPrivacyPage />} />
            <Route path="/mirror-index/admin" element={<DWAdminLoginPage />} />
            <Route path="/mirror-index/admin/dashboard" element={<DWAdminDashboardPage />} />

            {/* Legacy /dating-wellness/* — 301-style client redirects to /mirror-index/* (SEO + bookmarks) */}
            <Route path="/dating-wellness/*" element={<DatingWellnessLegacyRedirect />} />
            <Route path="/dating-wellness" element={<DatingWellnessLegacyRedirect />} />

            {/* Inkway / Quilliam Writing Platform */}
            <Route path="/inkway" element={<InkwayLanding />} />
            <Route path="/inkway/login" element={<InkwayLogin />} />
            <Route path="/inkway/register" element={<InkwayRegister />} />
            <Route path="/inkway/forgot-password" element={<InkwayForgotPassword />} />
            <Route path="/inkway/reset-password" element={<InkwayResetPassword />} />
            <Route path="/inkway/pricing" element={<InkwayPricing />} />
            <Route path="/inkway/concierge" element={<InkwayConciergeLanding />} />
            <Route path="/inkway/concierge/intake/:serviceId" element={<InkwayConciergeIntake />} />
            <Route path="/inkway/concierge/success" element={<InkwayConciergeSuccess />} />
            <Route path="/inkway/concierge/sla" element={<InkwayConciergeSLA />} />
            <Route path="/inkway/terms" element={<InkwayTerms />} />
            <Route path="/inkway/privacy" element={<InkwayPrivacy />} />
            <Route path="/inkway/onboarding" element={<InkwayProtectedRoute><InkwayOnboarding /></InkwayProtectedRoute>} />
            <Route path="/inkway/dashboard" element={<InkwayProtectedRoute><InkwayDashboard /></InkwayProtectedRoute>} />
            <Route path="/inkway/credits" element={<InkwayProtectedRoute><InkwayCreditHistory /></InkwayProtectedRoute>} />
            <Route path="/inkway/courses" element={<InkwayProtectedRoute><InkwayCourses /></InkwayProtectedRoute>} />
            <Route path="/inkway/courses/:courseId" element={<InkwayProtectedRoute><InkwayCourseDetail /></InkwayProtectedRoute>} />
            <Route path="/inkway/portal" element={<InkwayProtectedRoute><InkwayClientPortal /></InkwayProtectedRoute>} />
            <Route path="/inkway/admin" element={<InkwayAdminProtectedRoute><InkwayAdminLayout /></InkwayAdminProtectedRoute>}>
              <Route index element={<InkwayAdminDashboard />} />
              <Route path="pricing" element={<InkwayAdminPricing />} />
              <Route path="projects" element={<InkwayAdminProjects />} />
              <Route path="settings" element={<InkwayAdminSettings />} />
            </Route>
            
            <Route path="*" element={<Navigate to="/" replace />} />

            {/* Admin SSO landing — used by Super Admin "Open Admin Portal" flow */}
            <Route path="/admin-sso" element={<AdminSSOLanding />} />

            {/* Mira Luna — Standalone */}
            <Route path="/mira-luna" element={<MiraLunaLanding />} />
            <Route path="/mira-luna/about" element={<MiraLunaAbout />} />
            <Route path="/mira-luna/archetypes" element={<MiraLunaArchetypes />} />
            <Route path="/mira-luna/quiz/:quizType" element={<MiraLunaQuiz />} />
            <Route path="/mira-luna/results/:quizType/:resultId" element={<MiraLunaResults />} />
          </Routes>
          </Suspense>
        </BrowserRouter>
        </InkwayAuthProvider>
        </P150AuthProvider>
      </AdminAuthProvider>
    </DWAuthProvider>
    </AuthProvider>
    </MarketingCopyProvider>
    </HelmetProvider>
  );
}

export default App;
